import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';

import PioneerSummaryPanel from './PioneerSummaryPanel';
import {
  recordPioneerSummaryEvent,
} from '../services/pioneerSummaryApi';

import handIcon from '../assets/hand-icon.png';
import './HomePioneerExplorer.css';


const SETTLE_MS = 240;
const SWIPE_HINT_KEY = 'barterfi_pioneer_explorer_swipe_hint_seen';

const EXPLORER_ROTATION_MS = 6 * 60 * 60 * 1000;

const EXPLORER_CACHE_PREFIX = 'bf_pioneer_explorer_v4:';

const EXPLORER_FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'following', label: 'Following' },
  { id: 'followers', label: 'Followers' },
  { id: 'badge_holders', label: 'Badge holders' },
];

function normalizeUsername(value) {
  return String(value || '').trim().toLowerCase();
}

function explorerOrder(username, viewerUsername, windowKey) {
  const value =
    `${viewerUsername}:${windowKey}:${normalizeUsername(username)}`;

  let hash = 2166136261;

  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }

  return hash >>> 0;
}

function rotateExplorerPeople(people, viewerUsername, windowKey) {
  return [...people].sort((a, b) => {
    const difference =
      explorerOrder(a.username, viewerUsername, windowKey) -
      explorerOrder(b.username, viewerUsername, windowKey);

    return (
      difference ||
      normalizeUsername(a.username).localeCompare(
        normalizeUsername(b.username)
      )
    );
  });
}


export default function HomePioneerExplorer({ user, onClose }) {
  const navigate = useNavigate();
  const viewportRef = useRef(null);
  const gestureRef = useRef(null);
  const settleTimerRef = useRef(null);

  const [people, setPeople] = useState(() => [
    {
      username: user.username,
      name: user.name || user.username,
      profilePic: user.profilePic || '',
      group: 'self',
    },
  ]);

  const [index, setIndex] = useState(0);
  const [dragX, setDragX] = useState(0);
  const [direction, setDirection] = useState(0);
  const [settling, setSettling] = useState(false);
  const [followVersion, setFollowVersion] = useState(0);
  const [followBusy, setFollowBusy] = useState(false);

  const [showSwipeHint, setShowSwipeHint] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);

  const filteredPeople = people.filter((person) => {
    if (activeFilter === 'all') return true;
    if (person.group === 'self') return false;

    if (activeFilter === 'following') {
      return person.isFollowing === true;
    }

    if (activeFilter === 'followers') {
      return person.isFollower === true;
    }

    if (activeFilter === 'badge_holders') {
      return person.hasActiveBadge === true;
    }

    return false;
  });

  const selectFilter = (filterId) => {
    if (settleTimerRef.current) {
      window.clearTimeout(settleTimerRef.current);
      settleTimerRef.current = null;
    }

    gestureRef.current = null;
    setDragX(0);
    setDirection(0);
    setSettling(false);
    setIndex(0);
    setActiveFilter(filterId);
    setShowFilters(false);
    setShowSwipeHint(false);
  };

  useEffect(() => {
    let alive = true;

    const loadPeople = async () => {
      const viewerUsername = normalizeUsername(user.username);
      const windowKey = Math.floor(
        Date.now() / EXPLORER_ROTATION_MS
      );
      const cacheKey =
        `${EXPLORER_CACHE_PREFIX}${viewerUsername}`;

      const self = {
        username: user.username,
        name: user.name || user.username,
        profilePic: user.profilePic || '',
        group: 'self',
      };

      // Reuse this account's list for the current 6-hour window.
      try {
        const cached = JSON.parse(
          window.localStorage.getItem(cacheKey) || 'null'
        );

        const validCache =
          cached?.windowKey === windowKey &&
          Array.isArray(cached.people) &&
          cached.people.length > 0 &&
          cached.people.every(
            (person) =>
              person &&
              typeof person.username === 'string' &&
              normalizeUsername(person.username)
          ) &&
          normalizeUsername(cached.people[0].username) ===
            viewerUsername;

        if (validCache) {
          if (alive) {
            setPeople([self, ...cached.people.slice(1)]);
          }

          return;
        }
      } catch (error) {
        console.warn('Explorer cache unavailable:', error);
      }

      const [socialResult, suggestedResult] =
        await Promise.allSettled([
          api.get('/users/summary-explorer/people'),
          api.get('/suggested-pioneers'),
        ]);

      if (!alive) return;

      const socialPeople =
        socialResult.status === 'fulfilled' &&
        Array.isArray(socialResult.value.data?.people)
          ? socialResult.value.data.people
          : [];


      const suggestedPeople =
        suggestedResult.status === 'fulfilled' &&
        Array.isArray(suggestedResult.value.data?.users)
          ? suggestedResult.value.data.users
          : [];

      const badgeHolders =
        suggestedResult.status === 'fulfilled' &&
        Array.isArray(
          suggestedResult.value.data?.groups?.badgeHolders
        )
          ? suggestedResult.value.data.groups.badgeHolders
          : [];

      const seen = new Set();
      const combined = [];

      const addPerson = (person, group) => {
        const key = normalizeUsername(person?.username);
        if (!key) return;

        const flags = {
          isFollowing: person.isFollowing === true,
          isFollower: person.isFollower === true,
          hasActiveBadge:
            person.hasActiveBadge === true ||
            group === 'badge_holder',
        };

        const existing = combined.find(
          (item) => normalizeUsername(item.username) === key
        );

        if (existing) {
          existing.isFollowing =
            existing.isFollowing || flags.isFollowing;
          existing.isFollower =
            existing.isFollower || flags.isFollower;
          existing.hasActiveBadge =
            existing.hasActiveBadge || flags.hasActiveBadge;
          return;
        }

        seen.add(key);

        combined.push({
          username: person.username,
          name: person.name || person.username,
          profilePic: person.profilePic || '',
          group,
          ...flags,
        });
      };


      addPerson(self, 'self');


      socialPeople.forEach((person) => {
        if (person.group !== 'self') {
          addPerson(person, person.group);
        }
      });

      // Add current badge holders before general suggestions.
      const badgeSeen = new Set(seen);

      rotateExplorerPeople(
        badgeHolders,
        viewerUsername,
        windowKey
      )
        .filter((person) => {
          const key = normalizeUsername(person?.username);

          if (!key || badgeSeen.has(key)) return false;

          badgeSeen.add(key);
          return true;
        })
        .slice(0, 10)
        .forEach((person) => {
          addPerson(person, 'badge_holder');
        });

      // Rotate the full suggestion pool before taking 20.
      const suggestionSeen = new Set(seen);

      rotateExplorerPeople(
        suggestedPeople,
        viewerUsername,
        windowKey
      )
        .filter((person) => {
          const key = normalizeUsername(person?.username);

          if (!key || suggestionSeen.has(key)) return false;

          suggestionSeen.add(key);
          return true;
        })
        .slice(0, 20)
        .forEach((person) => {
          addPerson(person, 'suggested');
        });

      // Yourself first, then a mixture of the other groups.
      const finalPeople = [
        self,
        ...rotateExplorerPeople(
          combined.slice(1),
          viewerUsername,
          windowKey
        ),
      ];

      setPeople(finalPeople);

      // Retry on the next opening if either request failed.
      const bothRequestsSucceeded =
        socialResult.status === 'fulfilled' &&
        Array.isArray(socialResult.value.data?.people) &&
        suggestedResult.status === 'fulfilled' &&
        Array.isArray(suggestedResult.value.data?.users);

      if (bothRequestsSucceeded) {
        try {
          window.localStorage.setItem(
            cacheKey,
            JSON.stringify({
              windowKey,
              people: finalPeople,
            })
          );
        } catch (error) {
          console.warn('Could not save explorer list:', error);
        }
      }
    };

    loadPeople();

    return () => {
      alive = false;
    };
  }, [user.username, user.name, user.profilePic]);




  useEffect(() => {
    const oldOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = oldOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose]);

  /*
   * Show the swipe tutorial only once on this device.
   *
   * It appears shortly after the explorer opens, stays visible
   * for a few seconds, then disappears permanently.
   */
  useEffect(() => {
    let alreadySeen = false;

    try {
      alreadySeen =
        window.localStorage.getItem(SWIPE_HINT_KEY) === '1';
    } catch (error) {
      console.warn('Could not read swipe hint state:', error);
    }

    if (alreadySeen) return undefined;

    const showTimer = window.setTimeout(() => {
      setShowSwipeHint(true);
    }, 700);

    const hideTimer = window.setTimeout(() => {
      setShowSwipeHint(false);

      try {
        window.localStorage.setItem(SWIPE_HINT_KEY, '1');
      } catch (error) {
        console.warn('Could not save swipe hint state:', error);
      }
    }, 4700);

    return () => {
      window.clearTimeout(showTimer);
      window.clearTimeout(hideTimer);
    };
  }, []);

  useEffect(() => {
    return () => {
      if (settleTimerRef.current) {
        window.clearTimeout(settleTimerRef.current);
      }
    };
  }, []);

  const activeUsername = filteredPeople[index]?.username;

  useEffect(() => {
    if (!activeUsername) return;

    recordPioneerSummaryEvent(
      activeUsername,
      'summary_opened',
      'home'
    ).catch((err) => {
      console.warn(
        'Summary open event unavailable:',
        err?.response?.status || err?.message
      );
    });
  }, [activeUsername]);

  const width = () =>
    viewportRef.current?.clientWidth || window.innerWidth;

  const finishSlide = (nextIndex, finalX) => {
    setSettling(true);
    setDragX(finalX);

    settleTimerRef.current = window.setTimeout(() => {
      setIndex(nextIndex);
      setDragX(0);
      setDirection(0);
      setSettling(false);
      settleTimerRef.current = null;
    }, SETTLE_MS);
  };

  const handleTouchStart = (event) => {

    if (
      showFilters ||
      filteredPeople.length === 0 ||
      settling ||
      event.touches.length !== 1
    ) {
      return;
    }
    // Taps on actions and highlights should remain normal taps.
    if (
      event.target.closest(

  'button, a, input, textarea, select, .pioneer-summary-badges-row'
      )
    ) {
      return;
    }

    gestureRef.current = {
      x: event.touches[0].clientX,
      y: event.touches[0].clientY,
      axis: null,
    };
  };

  const handleTouchMove = (event) => {
    const gesture = gestureRef.current;

    if (
      !gesture ||
      settling ||
      event.touches.length !== 1
    ) {
      return;
    }

    const dx =
      event.touches[0].clientX - gesture.x;

    const dy =
      event.touches[0].clientY - gesture.y;

    if (
      !gesture.axis &&
      (Math.abs(dx) > 12 || Math.abs(dy) > 12)
    ) {
      gesture.axis =
        Math.abs(dx) > Math.abs(dy) * 1.25
          ? 'horizontal'
          : 'vertical';
    }

    if (gesture.axis !== 'horizontal') return;

    /*
     * If the user starts swiping, they already understand
     * the gesture. Hide the tutorial immediately and remember it.
     */
    if (showSwipeHint) {
      setShowSwipeHint(false);

      try {
        window.localStorage.setItem(
          SWIPE_HINT_KEY,
          '1'
        );
      } catch (error) {
        console.warn(
          'Could not save swipe hint state:',
          error
        );
      }
    }

    const nextDirection = dx < 0 ? 1 : -1;

    const hasNeighbor =
      nextDirection === 1

        ? index < filteredPeople.length - 1
        : index > 0;

    if (!hasNeighbor) {
      setDirection(0);
      setDragX(dx * 0.18);
      return;
    }

    setDirection(nextDirection);
    setDragX(dx);
  };

  const handleTouchEnd = () => {
    const gesture = gestureRef.current;
    gestureRef.current = null;

    if (
      !gesture ||
      gesture.axis !== 'horizontal'
    ) {
      return;
    }

    const distance = Math.abs(dragX);

    const shouldAdvance =
      direction !== 0 &&
      distance >= Math.min(90, width() * 0.22);

    if (shouldAdvance) {
      finishSlide(
        index + direction,
        -direction * width()
      );
    } else {
      finishSlide(index, 0);
    }
  };

  const handleFollow = async (person) => {
    if (
      !person ||
      person.group === 'self' ||
      followBusy
    ) {
      return;
    }

    try {
      setFollowBusy(true);

      await api.post(
        '/suggested-pioneers/toggle-follow',
        {
          username: person.username,
        }
      );

      setFollowVersion((value) => value + 1);
    } catch (error) {
      console.error(
        'Could not update follow:',
        error
      );
    } finally {
      setFollowBusy(false);
    }
  };

  const renderSlide = (personIndex, side) => {

    const person = filteredPeople[personIndex];
    if (!person) return null;

    const x = side * width() + dragX;
    const isCurrent = side === 0;

    return (
      <div
        key={`${person.username}-${followVersion}`}
        className={`home-explorer-slide ${
          settling ? 'is-settling' : ''
        }`}
        style={{
          transform: `translate3d(${x}px, 0, 0)`,
        }}
        aria-hidden={!isCurrent}
      >
        <PioneerSummaryPanel
          embedded
          username={person.username}
          entryPoint="home"
     
          onClose={onClose}
          onOpenFilters={() => setShowFilters(true)}
          filterActive={activeFilter !== 'all'}

          onFollow={() => handleFollow(person)}
          onMessage={() => {
            onClose();

            navigate(
              `/chat/${encodeURIComponent(
                person.username
              )}`
            );
          }}
          explorer={{
            group: person.group,
            position: personIndex + 1,

            total: filteredPeople.length,
          }}
        />
      </div>
    );
  };

  return (
    <div
      ref={viewportRef}
      className="home-explorer-viewport"
      role="dialog"
      aria-modal="true"
      aria-label="Explore Pioneer summaries"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onTouchCancel={handleTouchEnd}
    >

      {renderSlide(index - 1, -1)}
      {renderSlide(index, 0)}
      {renderSlide(index + 1, 1)}

      {filteredPeople.length === 0 && (
        <div className="home-explorer-empty">
          <h2>No Pioneers in this selection</h2>
          <p>Try another filter to explore more people.</p>

          <button
            type="button"
            onClick={() => setShowFilters(true)}
          >
            Change filter
          </button>

          <button type="button" onClick={onClose}>
            Close
          </button>
        </div>
      )}

      {showFilters && (
        <div
          className="home-explorer-filter-overlay"
          onClick={() => setShowFilters(false)}
        >
          <section
            className="home-explorer-filter-drawer"
            aria-label="Filter Pioneers"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="home-explorer-filter-heading">
              <h2>Filter Pioneers</h2>

              <button
                type="button"
                onClick={() => setShowFilters(false)}
                aria-label="Close filters"
                autoFocus
              >
                ×
              </button>
            </div>

            {EXPLORER_FILTERS.map((filter) => (
              <button
                key={filter.id}
                type="button"
                className={`home-explorer-filter-option ${
                  activeFilter === filter.id ? 'is-active' : ''
                }`}
                aria-pressed={activeFilter === filter.id}
                onClick={() => selectFilter(filter.id)}
              >
                <span>{filter.label}</span>
                {activeFilter === filter.id && (
                  <span aria-hidden="true">✓</span>
                )}
              </button>
            ))}
          </section>
        </div>
      )}

      {showSwipeHint && !showFilters && filteredPeople.length > 1 && (

        <div
          className="home-explorer-swipe-hint"
          aria-hidden="true"
        >
          <div className="home-explorer-swipe-hand">
            <img src={handIcon} alt="" />
          </div>

          <div className="home-explorer-swipe-text">
            Swipe left to explore Pioneers
          </div>
        </div>
      )}
    </div>
  );
}
